import { Eye, EyeOff } from "lucide-react"
import * as React from "react"
import { Navigate, useLocation } from "react-router-dom"

import { Checkbox } from "@/components/ui/checkbox"
import { ApiError, login, verifySecondFactor } from "@/lib/api"
import type { Enrollment, Identity } from "@/lib/api"
import { useAuth } from "@/lib/auth"
import { cn } from "@/lib/utils"

/**
 * Экран входа (AMEND-8 §3 шаг 7, внешний вид — AMEND-11).
 *
 * Здесь и только здесь консоль отступает от своей **industrial-utilitarian**
 * эстетики (обоснование — `tailwind.config.ts`). Причина не вкусовая: владелица
 * прислала снимок оригинального экрана SEON и попросила повторить его, чтобы
 * человек, которого пересаживают с вендорской панели на нашу, не спотыкался о
 * первый же экран. Приборный вид начинается за дверью, а дверь выглядит как у
 * вендора.
 *
 * Эстетика этих трёх экранов — **тёмная комната с одной освещённой карточкой**:
 * глухой синий во всю площадь, ничего кроме знака сверху и одной строки снизу,
 * и белый прямоугольник по центру, который забирает всё внимание. Цвета сняты
 * пипеткой с присланного снимка (`--auth-*` в `index.css`), а не подобраны на
 * глаз: фон `#080A3B`, знак `#10B9B2`, кнопка `#3F73FD`, заливка полей
 * `#E7F1FB`. Единственная намеренная «странность» — карточка не растёт: и на
 * первом ходу, и на экране кодов восстановления она одной ширины, меняется
 * только её содержимое, поэтому переход между ходами читается как смена
 * страницы в одном окне, а не как прыжок вёрстки.
 *
 * Тёмной шины с номерами `01 PASSWORD / 02 CODE` больше нет — на оригинале
 * такого счётчика не существует. Работу, которую она делала (человек понимает,
 * чего от него хотят во второй раз подряд), теперь делает собственный заголовок
 * каждого хода внутри той же карточки.
 *
 * Вход по-прежнему в два хода, потому что таковы ручки
 * (`services/admin_api/auth.py`): пароль ставит куку привязки, код открывает
 * сессию. Промежуточный токен живёт в куке `httpOnly` — на этой странице его
 * нет ни в состоянии, ни в поле, и подменить «за кого входим» между ходами
 * нечем. Правка AMEND-11 чисто визуальная: ни один ход, ни одна ручка и ни одна
 * проверка не тронуты.
 *
 * Про кнопки Google и Slack: это заглушки под будущий OAuth, и заглушки
 * **говорящие**. Кнопка, которая молча ничего не делает, хуже отсутствующей —
 * человек решает, что сломался вход, и пробует ещё раз. То же с
 * «Forgot password?»: ссылка на оригинале есть, почтового отправителя у нас
 * нет, поэтому она честно отвечает, что сброс делает админ аккаунта.
 */

type Stage =
  | { step: "password" }
  | { step: "code"; enrollment: Enrollment | null }
  | { step: "codes"; identity: Identity; codes: string[] }

export function LoginPage() {
  const { identity, checking, expired, signedIn } = useAuth()
  const location = useLocation()
  const [stage, setStage] = React.useState<Stage>({ step: "password" })
  const [codesSeen, setCodesSeen] = React.useState(false)

  // Куда возвращаемся после входа. `RequireAuth` кладёт сюда адрес, с которого
  // человека увели; прямой заход на `/login` такого состояния не несёт.
  const from = (location.state as { from?: string } | null)?.from
  const target = from && from !== "/login" ? from : "/scoring"

  // Уже вошли — экрану входа делать нечего. Кроме одного случая: набор кодов
  // восстановления показывается **после** открытия сессии, и увести с него
  // раньше времени значит потерять коды навсегда. Отсюда `codesSeen`: пока
  // человек не подтвердил, что записал коды, редирект ждёт; без этого флага
  // экран кодов становится тупиком — сессия открыта, а уйти с него нечем.
  if (identity && (codesSeen || stage.step !== "codes")) return <Navigate to={target} replace />

  return (
    <div className="auth-type flex min-h-screen flex-col bg-auth-bg px-4 py-8">
      <header className="flex shrink-0 justify-center">
        <Wordmark />
      </header>

      <main className="flex flex-1 items-center justify-center py-10">
        <div className="w-full max-w-[380px] animate-panel-in rounded-md bg-surface px-8 py-7 shadow-[0_18px_50px_-14px_rgb(0_0_0/0.55)]">
          {stage.step === "password" && (
            <PasswordStep
              expired={expired}
              checking={checking}
              onPassed={(enrollment) => setStage({ step: "code", enrollment })}
            />
          )}
          {stage.step === "code" && (
            <CodeStep
              enrollment={stage.enrollment}
              onBack={() => setStage({ step: "password" })}
              onVerified={(who, codes) => {
                if (codes && codes.length > 0) {
                  setStage({ step: "codes", identity: who, codes })
                  return
                }
                signedIn(who)
              }}
            />
          )}
          {stage.step === "codes" && (
            <RecoveryCodes
              codes={stage.codes}
              onDone={() => {
                setCodesSeen(true)
                signedIn(stage.identity)
              }}
            />
          )}
        </div>
      </main>

      {/* Вместо вендорского меню «Contact us | About us | Privacy notice» —
          одна строка тем же кеглем и цветом: у внутренней панели нет ни
          публичных страниц, ни формы обратной связи, и три ссылки в никуда
          были бы враньём в подвале. */}
      <footer className="shrink-0 pb-1 text-center text-[11px] tracking-wide text-auth-foot">
        For internal use only.
      </footer>
    </div>
  )
}

/**
 * Знак над карточкой.
 *
 * Векторного оригинала вендорского логотипа у нас нет и быть не должно, поэтому
 * берём круглый глиф в той же бирюзе и то же слово тем же весом: на снимке знак
 * читается как спираль в круге слева от надписи.
 */
function Wordmark() {
  return (
    <div className="flex items-center gap-2 text-auth-mark">
      {/* Спираль из вложенных полуокружностей с убывающим радиусом. Кольцо с
          закорючкой внутри давало бы «©» — знак читается как копирайт и рядом
          со словом paydetect выглядит опечаткой. */}
      <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
        className="h-6 w-6"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
      >
        <path d="M2 12A10 10 0 0 1 22 12A8.5 8.5 0 0 1 5 12A6.5 6.5 0 0 1 18 12A5 5 0 0 1 8 12A3.5 3.5 0 0 1 15 12" />
      </svg>
      <span className="text-xl font-semibold tracking-[0.06em]">paydetect</span>
    </div>
  )
}

/* ------------------------------------------------------------------------- */
/* Элементы карточки. Живут здесь, а не в `components/ui`, намеренно: у консоли */
/* кнопки и поля другие (низкие, трафаретные, с жёсткой тенью), и общий         */
/* компонент пришлось бы разветвлять на два несовместимых вида.                */
/* ------------------------------------------------------------------------- */

function Heading({ title, lines }: { title: string; lines: string[] }) {
  return (
    <div className="text-center">
      <h1 className="text-xl font-semibold tracking-[-0.01em] text-auth-head">{title}</h1>
      <p className="mt-1.5 text-[13px] leading-5 text-auth-sub">
        {lines.map((line) => (
          <React.Fragment key={line}>
            {line}
            <br />
          </React.Fragment>
        ))}
      </p>
    </div>
  )
}

const AuthInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({ className, ...props }, ref) => (
    <input
      ref={ref}
      className={cn(
        "h-[38px] w-full rounded border border-auth-field-line bg-auth-field px-3 text-[13px] text-auth-head outline-none transition-colors duration-150 ease-snap",
        "placeholder:text-auth-sub focus:border-auth-cta disabled:cursor-not-allowed disabled:opacity-60",
        className,
      )}
      {...props}
    />
  ),
)
AuthInput.displayName = "AuthInput"

function AuthButton({
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      className={cn(
        "h-[38px] w-full rounded bg-auth-cta text-[13px] font-medium text-white transition-colors duration-150 ease-snap",
        "hover:bg-auth-cta/90 active:bg-auth-cta disabled:pointer-events-none disabled:opacity-50",
        className,
      )}
      {...props}
    />
  )
}

/** Тихая ссылка карточки: «Forgot password?», «Start over». */
function QuietLink({ className, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={cn(
        "text-[12px] text-auth-cta underline-offset-2 transition-colors duration-150 ease-snap hover:underline",
        className,
      )}
      {...props}
    />
  )
}

/** Строка-пояснение внутри карточки: отказ ручки или ответ заглушки. */
function AuthNote({ tone, children }: { tone: "error" | "info"; children: React.ReactNode }) {
  return (
    <p
      role={tone === "error" ? "alert" : "status"}
      className={cn(
        "mt-3 rounded border px-3 py-2 text-[12px] leading-relaxed",
        tone === "error"
          ? "border-decline/30 bg-decline/5 text-decline"
          : "border-auth-field-line bg-auth-field text-auth-sub",
      )}
    >
      {children}
    </p>
  )
}

function Divider({ label }: { label: string }) {
  return (
    <div className="mt-5 flex items-center gap-3">
      <span className="h-px flex-1 bg-auth-field-line" />
      <span className="text-[11px] text-auth-sub">{label}</span>
      <span className="h-px flex-1 bg-auth-field-line" />
    </div>
  )
}

/* ------------------------------------------------------------------------- */

function PasswordStep({
  expired,
  checking,
  onPassed,
}: {
  expired: boolean
  checking: boolean
  onPassed: (enrollment: Enrollment | null) => void
}) {
  const [email, setEmail] = React.useState("")
  const [password, setPassword] = React.useState("")
  const [shown, setShown] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [note, setNote] = React.useState<string | null>(null)
  const [busy, setBusy] = React.useState(false)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    setNote(null)
    try {
      const result = await login(email.trim(), password)
      // Пароль из состояния убираем сразу, как только он больше не нужен:
      // дальше ходом правит кука привязки, а строка в памяти вкладки — лишний
      // экземпляр секрета, который переживёт весь второй шаг.
      setPassword("")
      onPassed(result.enrollment)
    } catch (cause: unknown) {
      setError(messageFor(cause))
      setBusy(false)
    }
  }

  return (
    <>
      <Heading title="Welcome back" lines={["It's great to see you.", "Log in to your account below."]} />

      {expired && <AuthNote tone="info">Your session ended. Log in again — nothing you had open was lost.</AuthNote>}

      <form onSubmit={submit} className="mt-6">
        <label htmlFor="login-email" className="sr-only">
          Work email
        </label>
        <AuthInput
          id="login-email"
          type="email"
          name="email"
          value={email}
          autoComplete="username"
          autoFocus
          required
          disabled={busy || checking}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="name@company.com"
        />

        <label htmlFor="login-password" className="sr-only">
          Password
        </label>
        <div className="relative mt-2.5">
          <AuthInput
            id="login-password"
            type={shown ? "text" : "password"}
            name="password"
            value={password}
            autoComplete="current-password"
            required
            disabled={busy || checking}
            onChange={(event) => setPassword(event.target.value)}
            placeholder="Password"
            className="pr-10"
          />
          <button
            type="button"
            onClick={() => setShown((value) => !value)}
            aria-label={shown ? "Hide password" : "Show password"}
            className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-auth-sub transition-colors duration-150 ease-snap hover:text-auth-head"
          >
            {shown ? <EyeOff className="h-4 w-4" strokeWidth={1.9} /> : <Eye className="h-4 w-4" strokeWidth={1.9} />}
          </button>
        </div>

        {error && <AuthNote tone="error">{error}</AuthNote>}

        <AuthButton type="submit" className="mt-3.5" disabled={busy || checking}>
          {busy ? "Logging in…" : "Log in"}
        </AuthButton>
      </form>

      <div className="mt-3 text-center">
        <QuietLink
          onClick={() =>
            setNote("Password resets go through your account admin — this build has no mail sender.")
          }
        >
          Forgot password?
        </QuietLink>
      </div>

      <Divider label="Or continue with" />
      <Providers onStub={(name) => setNote(`${name} sign-in is not connected yet. Log in with your email and password.`)} />

      {note && <AuthNote tone="info">{note}</AuthNote>}
    </>
  )
}

/**
 * Вход через сервисы — заглушки под будущий OAuth (AMEND-11).
 *
 * Microsoft и SSO с оригинала сняты: владелица назвала только эти две. Обе —
 * обычный authorization-code flow, поэтому разметка стоит заранее, а вот
 * молчаливого нажатия здесь нет: кнопка отвечает словами, что она ещё не
 * подключена.
 */
const PROVIDERS = [
  {
    id: "google",
    name: "Google",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true" className="h-[18px] w-[18px]">
        <path
          fill="#4285F4"
          d="M23.06 12.25c0-.85-.08-1.67-.22-2.45H12v4.64h6.2a5.3 5.3 0 0 1-2.3 3.48v2.89h3.72c2.18-2 3.44-4.96 3.44-8.56Z"
        />
        <path
          fill="#34A853"
          d="M12 23.5c3.11 0 5.72-1.03 7.62-2.79l-3.72-2.89c-1.03.69-2.35 1.1-3.9 1.1-3 0-5.54-2.02-6.45-4.75H1.7v2.98A11.5 11.5 0 0 0 12 23.5Z"
        />
        <path fill="#FBBC05" d="M5.55 14.17a6.9 6.9 0 0 1 0-4.34V6.85H1.7a11.5 11.5 0 0 0 0 10.3l3.85-2.98Z" />
        <path
          fill="#EA4335"
          d="M12 4.75c1.69 0 3.21.58 4.4 1.72l3.3-3.3C17.72 1.3 15.11.25 12 .25 7.52.25 3.64 2.82 1.7 6.85l3.85 2.98C6.46 7.1 9 4.75 12 4.75Z"
        />
      </svg>
    ),
  },
  {
    id: "slack",
    name: "Slack",
    icon: (
      <svg viewBox="0 0 122.8 122.8" aria-hidden="true" className="h-[18px] w-[18px]">
        <path
          fill="#E01E5A"
          d="M25.8 77.6c0 7.1-5.8 12.9-12.9 12.9S0 84.7 0 77.6s5.8-12.9 12.9-12.9h12.9v12.9zm6.5 0c0-7.1 5.8-12.9 12.9-12.9s12.9 5.8 12.9 12.9v32.3c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9V77.6z"
        />
        <path
          fill="#36C5F0"
          d="M45.2 25.8c-7.1 0-12.9-5.8-12.9-12.9S38.1 0 45.2 0s12.9 5.8 12.9 12.9v12.9H45.2zm0 6.5c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9H12.9C5.8 58.1 0 52.3 0 45.2s5.8-12.9 12.9-12.9h32.3z"
        />
        <path
          fill="#2EB67D"
          d="M97 45.2c0-7.1 5.8-12.9 12.9-12.9s12.9 5.8 12.9 12.9-5.8 12.9-12.9 12.9H97V45.2zm-6.5 0c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9V12.9C64.7 5.8 70.5 0 77.6 0s12.9 5.8 12.9 12.9v32.3z"
        />
        <path
          fill="#ECB22E"
          d="M77.6 97c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9-12.9-5.8-12.9-12.9V97h12.9zm0-6.5c-7.1 0-12.9-5.8-12.9-12.9s5.8-12.9 12.9-12.9h32.3c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9H77.6z"
        />
      </svg>
    ),
  },
] as const

function Providers({ onStub }: { onStub: (name: string) => void }) {
  return (
    <>
      <div className="mt-3 grid grid-cols-2 overflow-hidden rounded border border-auth-field-line">
        {PROVIDERS.map((provider, index) => (
          <button
            key={provider.id}
            type="button"
            onClick={() => onStub(provider.name)}
            className={cn(
              "flex h-[46px] items-center justify-center transition-colors duration-150 ease-snap hover:bg-auth-field",
              index > 0 && "border-l border-auth-field-line",
            )}
          >
            {provider.icon}
            <span className="sr-only">Continue with {provider.name}</span>
          </button>
        ))}
      </div>
      <div aria-hidden="true" className="mt-1.5 grid grid-cols-2 text-center text-[11px] text-auth-sub">
        {PROVIDERS.map((provider) => (
          <span key={provider.id}>{provider.name}</span>
        ))}
      </div>
    </>
  )
}

function CodeStep({
  enrollment,
  onBack,
  onVerified,
}: {
  enrollment: Enrollment | null
  onBack: () => void
  onVerified: (identity: Identity, codes: string[] | null) => void
}) {
  const [code, setCode] = React.useState("")
  const [error, setError] = React.useState<string | null>(null)
  const [busy, setBusy] = React.useState(false)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const result = await verifySecondFactor(code.trim())
      onVerified(result.identity, result.recovery_codes)
    } catch (cause: unknown) {
      setError(messageFor(cause))
      setCode("")
      setBusy(false)
    }
  }

  return (
    <>
      <Heading
        title={enrollment ? "Set up your authenticator" : "One more step"}
        lines={
          enrollment
            ? ["Scan the code with your authenticator app,", "then enter the six digits it shows."]
            : ["Enter the six digits from your", "authenticator app."]
        }
      />

      {enrollment && <EnrollmentPanel enrollment={enrollment} />}

      <form onSubmit={submit} className={enrollment ? "mt-4" : "mt-6"}>
        <label htmlFor="login-code" className="sr-only">
          Authenticator code
        </label>
        <AuthInput
          id="login-code"
          name="code"
          value={code}
          autoComplete="one-time-code"
          autoFocus
          required
          maxLength={32}
          disabled={busy}
          onChange={(event) => setCode(event.target.value)}
          className="text-center font-mono text-base tracking-[0.3em]"
          placeholder="000000"
        />
        <p className="mt-1.5 text-center text-[11px] leading-relaxed text-auth-sub">
          Six digits, or one recovery code (<span className="font-mono">abcd-efgh-jkmn</span>).
        </p>

        {error && <AuthNote tone="error">{error}</AuthNote>}

        <AuthButton type="submit" className="mt-3.5" disabled={busy}>
          {busy ? "Verifying…" : "Log in"}
        </AuthButton>
      </form>

      <div className="mt-3 text-center">
        <QuietLink onClick={onBack} disabled={busy}>
          Start over
        </QuietLink>
      </div>
    </>
  )
}

/**
 * Привязка приложения: показывается, пока `totp_enabled` ложно.
 *
 * QR приезжает от ручки готовой картинкой SVG и вставляется **как картинка по
 * data-URI**, а не разметкой через `dangerouslySetInnerHTML`: пришедший от
 * сервера SVG внутри DOM страницы — это исполняемый `<script>` внутри него, а
 * внутри `<img>` скрипты не работают вовсе.
 */
function EnrollmentPanel({ enrollment }: { enrollment: Enrollment }) {
  const source = `data:image/svg+xml;utf8,${encodeURIComponent(enrollment.qr_svg)}`
  return (
    <div className="mt-5 flex flex-col items-center">
      <img
        src={source}
        alt="Authenticator setup QR code"
        width={160}
        height={160}
        className="h-40 w-40 rounded border border-auth-field-line bg-white p-1.5"
      />
      <p className="mt-2 text-center text-[11px] leading-relaxed text-auth-sub">
        The code is shown once — after this login it is gone.
      </p>
    </div>
  )
}

/**
 * Коды восстановления — единственный экран, где они существуют в открытом виде.
 *
 * Уйти с него можно только сознательно: галочка снимает блокировку с кнопки.
 * Автопереход в консоль здесь был бы потерей доступа — в базе лежат хеши, и
 * показать набор второй раз не может никто, включая нас.
 */
function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const [saved, setSaved] = React.useState(false)

  return (
    <>
      <Heading
        title="Save your recovery codes"
        lines={["Each code logs you in once if you lose", "your phone. Shown now and never again."]}
      />

      <ul className="mt-5 grid grid-cols-2 gap-x-3 gap-y-1 rounded border border-auth-field-line bg-auth-field px-3 py-2.5 font-mono text-[12px] text-auth-head">
        {codes.map((code) => (
          <li key={code} className="tabular-nums tracking-tight">
            {code}
          </li>
        ))}
      </ul>

      <label className="mt-4 flex cursor-pointer items-start gap-2 text-[12px] leading-5 text-auth-head">
        <Checkbox
          checked={saved}
          onCheckedChange={(value) => setSaved(value === true)}
          className="mt-0.5 border-auth-field-line data-[state=checked]:border-auth-cta data-[state=checked]:bg-auth-cta"
        />
        <span>I have written these down somewhere safe.</span>
      </label>

      <AuthButton className="mt-4" disabled={!saved} onClick={onDone}>
        Open the console
      </AuthButton>
    </>
  )
}

/**
 * Текст отказа — словами ручки, без пересказа.
 *
 * Бэкенд отвечает на «нет такой почты» и «пароль не тот» одинаково намеренно
 * (иначе перебор собирает список сотрудников), и придумывать здесь более
 * «дружелюбную» формулировку значило бы снимать эту защиту на фронте.
 */
function messageFor(cause: unknown): string {
  if (cause instanceof ApiError) return cause.message
  return "The console could not reach the API. Check the connection and try again."
}
